import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { db, auth, database } from '../firebase';
import Avatar from './Avatar';
import '../styles/global.css';
import '../styles/Profile.css';
import LoadingSpinner from './LoadingSpinner';

const AVATAR_EMOJIS = ["😀", "🦁", "🐱", "🐶", "🐵", "🐸", "🐼", "🐧", "🦊", "🐻"];
const AVATAR_GRAPHICS = Array.from({ length: 21 }, (_, i) => `avatar_${i + 1}`);

const Profile = () => {
    const [profile, setProfile] = useState({
        uid: '',
        displayName: '',
        photoUrl: '',
        bio: '',
        interests: [],
        grado: '',
        cuerpoServicio: '',
        especialidad: '',
        jerarquia: '',
        isPremium: false,
        premiumEndDate: null,
        avatar: '👤',
        createdAt: null,
        lastOperationalUpdate: null
    });
    const [metadata, setMetadata] = useState({
        cuerpos: [],
        especialidades: {},
        grados: []
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isEditingOperational, setIsEditingOperational] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [canEditOperational, setCanEditOperational] = useState(true);
    const [profileLocked, setProfileLocked] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);
    const [showAvatarSelector, setShowAvatarSelector] = useState(false);

    const formatDate = (timestamp) => {
        if (timestamp === 0) return 'Acceso Permanente';
        if (!timestamp) return 'S/I';
        // Handle Firestore Timestamp, Date object, or milliseconds
        let date;
        if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    useEffect(() => {
        loadProfile();
        loadMetadata();
    }, []);

    const loadMetadata = async () => {
        try {
            const metadataRef = ref(database, 'app_metadata');
            const snapshot = await get(metadataRef);
            if (snapshot.exists()) {
                const data = snapshot.val();
                setMetadata({
                    cuerpos: data.cuerpos_y_servicios ? Object.values(data.cuerpos_y_servicios) : [],
                    especialidades: data.especialidades || {},
                    grados: data.grados ? Object.values(data.grados) : []
                });
            }
        } catch (error) {
            console.error('Error loading metadata:', error);
        }
    };

    const loadProfile = async () => {
        if (!auth.currentUser) {
            setLoading(false);
            return;
        }

        try {
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setProfile({
                    uid: auth.currentUser.uid,
                    displayName: userData.nick || userData.displayName || auth.currentUser.displayName || '',
                    photoUrl: userData.photoUrl || auth.currentUser.photoURL || '',
                    avatar: userData.avatar || '👤',
                    bio: userData.bio || '',
                    interests: userData.interests || [],
                    grado: userData.grado || '',
                    cuerpoServicio: userData.cuerpoServicio || userData.cuerpo || '',
                    especialidad: userData.especialidad || '',
                    jerarquia: userData.jerarquia || userData.grado || '',
                    isPremium: userData.isPremium || false,
                    premiumEndDate: userData.premiumEndDate || null,
                    createdAt: userData.createdAt || auth.currentUser.metadata.creationTime,
                    lastOperationalUpdate: userData.lastOperationalUpdate || null
                });

                // Bloqueo permanente si ya tiene datos operativos guardados
                const hasOperationalData = userData.jerarquia || userData.cuerpoServicio || userData.especialidad;
                if (hasOperationalData) {
                    setCanEditOperational(false);
                    setProfileLocked(true);
                } else {
                    setCanEditOperational(true);
                    setIsEditingOperational(true);
                }
            } else {
                setProfile({
                    uid: auth.currentUser.uid,
                    displayName: auth.currentUser.displayName || 'Usuario Vortex',
                    photoUrl: auth.currentUser.photoURL || '',
                    avatar: '👤',
                    bio: '',
                    interests: [],
                    grado: '',
                    cuerpoServicio: '',
                    especialidad: '',
                    isPremium: false,
                    premiumEndDate: null,
                    createdAt: auth.currentUser.metadata.creationTime
                });
                setCanEditOperational(true);
                setIsEditingOperational(true);
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            setError('Error al cargar el perfil operativo');
        }
        setLoading(false);
    };

    const handleSave = async () => {
        if (!auth.currentUser) return;

        setIsSaving(true);
        setError('');
        setSuccess('');

        try {
            // Update Firebase Auth profile
            await updateProfile(auth.currentUser, {
                displayName: profile.displayName,
                photoURL: profile.photoUrl
            });

            // Update Firestore profile
            await setDoc(doc(db, 'users', auth.currentUser.uid), {
                nick: profile.displayName,
                photoUrl: profile.photoUrl,
                avatar: profile.avatar,
                bio: profile.bio,
                interests: profile.interests,
                updatedAt: new Date()
            }, { merge: true });

            setSuccess('Perfil sincronizado correctamente');
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving profile:', error);
            setError('Error en la sincronización de datos');
        }
        setIsSaving(false);
    };

    const handleSaveOperational = async () => {
        if (!auth.currentUser) return;
        setIsSaving(true);
        setError('');
        setSuccess('');

        try {
            await setDoc(doc(db, 'users', auth.currentUser.uid), {
                cuerpoServicio: profile.cuerpoServicio,
                especialidad: profile.especialidad,
                jerarquia: profile.jerarquia,
                grado: profile.jerarquia,
                lastOperationalUpdate: serverTimestamp()
            }, { merge: true });

            setSuccess('Perfil guardado correctamente. No podrá modificarse una vez confirmado.');
            setCanEditOperational(false);
            setProfileLocked(true);
            setIsEditingOperational(false);
        } catch (error) {
            console.error('Error saving operational profile:', error);
            setError('Error al actualizar los datos operativos');
        }
        setIsSaving(false);
    };

    const handleInterestAdd = (interest) => {
        if (interest.trim() && !profile.interests.includes(interest.trim())) {
            setProfile(prev => ({
                ...prev,
                interests: [...prev.interests, interest.trim()]
            }));
        }
    };

    const handleInterestRemove = (interest) => {
        setProfile(prev => ({
            ...prev,
            interests: prev.interests.filter(i => i !== interest)
        }));
    };

    const handleCancel = () => {
        loadProfile();
        setIsEditing(false);
        setError('');
        setSuccess('');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                   <LoadingSpinner label="Cargando datos..." height="300px" />
            </div>
        );
    }

    return (
        <div className="profile-wrapper" style={{ maxWidth: '100%', margin: 0, height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto' }}>
            <div className="profile-header-flex">
                <div>
                    <h1 className="profile-header-title">Ficha Operativa</h1>
                    <p className="profile-header-desc">Centro de control de identidad y estado de ascenso</p>
                </div>
                {!isEditing ? (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="btn-primary"
                        style={{ padding: '12px 25px', borderRadius: '12px', fontWeight: '700' }}
                    >
                        Editar Perfil
                    </button>
                ) : (
                    <div className="flex gap-md">
                        <button
                            onClick={handleCancel}
                            disabled={isSaving}
                            className="btn-secondary"
                            style={{ padding: '12px 25px', borderRadius: '12px' }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="btn-primary"
                            style={{ padding: '12px 25px', borderRadius: '12px', fontWeight: '700' }}
                        >
                            {isSaving ? 'Sincronizando...' : 'confirmar Cambios'}
                        </button>
                    </div>
                )}
            </div>

            {error && <div className="error-message" style={{ marginBottom: '20px' }}>⚠️ {error}</div>}
            {success && <div className="card" style={{ background: 'rgba(0, 242, 254, 0.1)', color: 'var(--accent)', padding: '1rem', textAlign: 'center', marginBottom: '1.5rem', border: '1px solid var(--accent)', borderRadius: '12px' }}>✅ {success}</div>}

            <div className="profile-grid">
                {/* Lateral Column - Profile Image and Main Status */}
                <div className="profile-sidebar" style={{ zIndex: showAvatarSelector ? 100 : 1 }}>
                    <div className="card profile-sidebar-card">
                        {profile.isPremium && (
                            <div className="premium-badge">
                                PREMIUM ⭐
                            </div>
                        )}

                        <div className="avatar-container">
                            <Avatar src={profile.avatar} size={150} />

                            {isEditing && (
                                <button
                                    className="edit-avatar-btn"
                                    onClick={() => setShowAvatarSelector(!showAvatarSelector)}
                                >
                                    ✏️
                                </button>
                            )}
                        </div>

                        {showAvatarSelector && isEditing && (
                            <div className="avatar-selector-panel">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <h4 style={{ margin: 0, color: 'var(--accent)', fontSize: '0.9rem' }}>Elige tu Identidad</h4>
                                    <button onClick={() => setShowAvatarSelector(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>✕</button>
                                </div>

                                <div className="avatar-scroll">
                                    {AVATAR_GRAPHICS.map(av => (
                                        <div
                                            key={av}
                                            onClick={() => {
                                                setProfile({ ...profile, avatar: av });
                                                setShowAvatarSelector(false);
                                            }}
                                            className={`avatar-item ${profile.avatar === av ? 'selected' : ''}`}
                                        >
                                            <Avatar src={av} size={45} />
                                        </div>
                                    ))}
                                </div>

                                <div className="emoji-scroll">
                                    {AVATAR_EMOJIS.map(emoji => (
                                        <span
                                            key={emoji}
                                            onClick={() => {
                                                setProfile({ ...profile, avatar: emoji });
                                                setShowAvatarSelector(false);
                                            }}
                                            className={`emoji-item ${profile.avatar === emoji ? 'selected' : ''}`}
                                        >
                                            {emoji}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <h2 style={{ fontSize: '1.5rem', marginBottom: '5px' }}>{profile.displayName || 'Vortex User'}</h2>
                        <p style={{ color: 'var(--accent)', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '2px' }}>
                            {profile.grado}
                        </p>

                        <div className="profile-stats-list">
                            <div className="profile-stat-row">
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>ID de Usuario</span>
                                <code style={{ fontSize: '0.7rem', opacity: 0.5 }}>{profile.uid.substring(0, 10)}...</code>
                            </div>
                            <div className="profile-stat-row">
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Antigüedad</span>
                                <span style={{ fontSize: '0.8rem', fontWeight: '600' }}>{formatDate(profile.createdAt)}</span>
                            </div>
                            {profile.isPremium && (
                                <div className="profile-stat-row">
                                    <span style={{ fontSize: '0.8rem', color: 'gold', fontWeight: '700' }}>Expira</span>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '600', color: 'gold' }}>{formatDate(profile.premiumEndDate)}</span>
                                </div>
                            )}
                        </div>

                        <a
                            href="https://billing.stripe.com/p/login/dRm28r45W4Kmeo2bHz73G00"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary"
                            style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '12px', fontSize: '0.8rem' }}
                        >
                            Cancelar suscripción
                        </a>
                    </div>
                </div>

                {/* Main Content - Personal Info and Settings */}
                <div className="card profile-main-card">
                    <div style={{ display: 'grid', gap: '2.5rem' }}>

                        {/* Information Grid */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 className="profile-section-title">Información de Combate</h3>
                            {canEditOperational && !isEditingOperational ? (
                                <button 
                                    onClick={() => setIsEditingOperational(true)}
                                    className="edit-operational-btn"
                                    title="Configurar perfil"
                                >
                                    ✏️
                                </button>
                            ) : profileLocked && (
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                    🔒 Perfil bloqueado permanentemente
                                </span>
                            )}
                        </div>

                        {isEditingOperational ? (
                            <div className="card" style={{ padding: '20px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--accent)', borderRadius: '15px' }}>
                                <div className="operational-grid">
                                    <div className="form-group">
                                        <label htmlFor="profile-cuerpo" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Cuerpo / Servicio</label>
                                        <select 
                                            id="profile-cuerpo"
                                            value={profile.cuerpoServicio}
                                            onChange={(e) => setProfile(prev => ({ ...prev, cuerpoServicio: e.target.value, especialidad: '' }))}
                                            className="form-input"
                                            style={{ width: '100%', padding: '12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--accent)', fontWeight: '600' }}
                                        >
                                            <option value="">Seleccione...</option>
                                            {metadata.cuerpos.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="profile-especialidad" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Especialidad</label>
                                        <select 
                                            id="profile-especialidad"
                                            value={profile.especialidad}
                                            onChange={(e) => setProfile(prev => ({ ...prev, especialidad: e.target.value }))}
                                            className="form-input"
                                            style={{ width: '100%', padding: '12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--accent)', fontWeight: '600' }}
                                            disabled={!profile.cuerpoServicio}
                                        >
                                            <option value="">Seleccione...</option>
                                            {profile.cuerpoServicio && metadata.especialidades[profile.cuerpoServicio]?.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group grid-span-2">
                                        <label htmlFor="profile-jerarquia" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Jerarquía</label>
                                        <select 
                                            id="profile-jerarquia"
                                            value={profile.jerarquia}
                                            onChange={(e) => setProfile(prev => ({ ...prev, jerarquia: e.target.value }))}
                                            className="form-input"
                                            style={{ width: '100%', padding: '12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--accent)', fontWeight: '600' }}
                                        >
                                            <option value="">Seleccione...</option>
                                            {metadata.grados.map(g => <option key={g} value={g}>{g}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex gap-md" style={{ marginTop: '20px', justifyContent: 'flex-end' }}>
                                    <button onClick={() => setIsEditingOperational(false)} className="btn-secondary" style={{ padding: '8px 20px', borderRadius: '8px', fontSize: '0.85rem' }}>Cancelar</button>
                                    <button onClick={handleSaveOperational} disabled={isSaving || !profile.jerarquia} className="btn-primary" style={{ padding: '8px 20px', borderRadius: '8px', fontSize: '0.85rem' }}>
                                        {isSaving ? 'Guardando...' : 'Guardar y Bloquear'}
                                    </button>
                                </div>
                                <p style={{ fontSize: '0.65rem', color: '#888', marginTop: '10px', fontStyle: 'italic', textAlign: 'center' }}>
                                    ⚠️ Una vez guardado, el perfil no podrá modificarse.
                                </p>
                            </div>
                        ) : (
                            <div className="operational-grid">
                                <div className="form-group">
                                    <label style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>
                                        Cuerpo / Servicio
                                    </label>
                                    <div className="operational-display">
                                        {profile.cuerpoServicio || 'No especificado'}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>
                                        Especialidad
                                    </label>
                                    <div className="operational-display">
                                        {profile.especialidad || 'No especificada'}
                                    </div>
                                </div>
                                <div className="form-group grid-span-2">
                                    <label style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>
                                        Jerarquía
                                    </label>
                                    <div className="operational-display">
                                        {profile.jerarquia || profile.grado || 'No especificada'}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Display Name Input */}
                        <div className="form-group">
                            <label htmlFor="profile-nickname" style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'block' }}>
                                Nickname / Alias de Red
                            </label>
                            {isEditing ? (
                                <input
                                    id="profile-nickname"
                                    type="text"
                                    value={profile.displayName}
                                    onChange={(e) => setProfile(prev => ({ ...prev, displayName: e.target.value }))}
                                    className="form-input"
                                    style={{
                                        width: '100%',
                                        padding: '15px',
                                        background: 'rgba(255,255,255,0.05)',
                                        border: '1px solid var(--accent)',
                                        color: 'white',
                                        borderRadius: '12px',
                                        fontSize: '1rem'
                                    }}
                                />
                            ) : (
                                <div style={{ fontSize: '1.2rem', fontWeight: '500' }}>
                                    {profile.displayName || 'Anónimo'}
                                </div>
                            )}
                        </div>

                        {/* Bio Input */}
                        <div className="form-group">
                            <label htmlFor="profile-bio" style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'block' }}>
                                Estrategia Personal / Bio
                            </label>
                            {isEditing ? (
                                <textarea
                                    id="profile-bio"
                                    value={profile.bio}
                                    onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                                    rows={4}
                                    placeholder="Describa su enfoque operativo..."
                                    style={{
                                        width: '100%',
                                        padding: '15px',
                                        background: 'rgba(255,255,255,0.05)',
                                        border: '1px solid var(--accent)',
                                        color: 'white',
                                        borderRadius: '12px',
                                        fontSize: '1rem',
                                        resize: 'none',
                                        lineHeight: '1.5'
                                    }}
                                />
                            ) : (
                                <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                                    {profile.bio || 'Sin datos biográficos registrados en el sistema.'}
                                </p>
                            )}
                        </div>

                        {/* Interests Selection */}
                        <div className="form-group">
                            <label style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'block' }}>
                                Especializaciones de Interés
                            </label>
                            {isEditing ? (
                                <div>
                                    <div className="flex gap-md" style={{ marginBottom: '15px' }}>
                                        <input
                                            type="text"
                                            aria-label="Nueva especialización"
                                            placeholder="Nueva especialización..."
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleInterestAdd(e.target.value);
                                                    e.target.value = '';
                                                }
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                background: 'rgba(255,255,255,0.05)',
                                                border: '1px solid var(--border)',
                                                color: 'white',
                                                borderRadius: '10px'
                                            }}
                                        />
                                        <button
                                            onClick={(e) => {
                                                const input = e.currentTarget.previousElementSibling;
                                                handleInterestAdd(input.value);
                                                input.value = '';
                                            }}
                                            className="btn-primary"
                                            style={{ padding: '10px 20px', fontSize: '0.8rem' }}
                                        >
                                            Añadir
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-md">
                                        {profile.interests.map((interest, index) => (
                                            <div
                                                key={index}
                                                className="interest-tag"
                                            >
                                                {interest}
                                                <button
                                                    type="button"
                                                    aria-label={`Eliminar interés ${interest}`}
                                                    onClick={() => handleInterestRemove(interest)}
                                                    style={{ cursor: 'pointer', fontSize: '1.2rem', fontWeight: '400', background: 'none', border: 'none', color: 'inherit', padding: 0, lineHeight: 1 }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-md">
                                    {profile.interests.length > 0 ? (
                                        profile.interests.map((interest, index) => (
                                            <span
                                                key={index}
                                                className="interest-tag-display"
                                            >
                                                {interest}
                                            </span>
                                        ))
                                    ) : (
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>No se han registrado especializaciones de interés.</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;